import React from 'react';
import { TextareaElement, Wrapper } from 'src/domains/layouts/shared/formComponents/textarea/Textarea.style';
// @ts-expect-error
import CKEditor from 'react-ckeditor-component';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { observer } from 'src/utils/mobx-react';
import { InputError, InputLabel } from 'src/domains/layouts/shared/formComponents/FormComponents.style';

interface PropsType {
    label?: string;
    placeholder?: string;
    formState: FormInputState<string, unknown>;
    isError?: boolean;
    isEmpty?: boolean;
    maxLength?: number;
    required?: boolean;
    ckeditor?: boolean;
    dataTest?: string;
    isReadOnly?: boolean;
    customErrorMessage?: React.JSX.Element;
    config?: Record<string, unknown>;
}

export const hideErrorMessage = (): void => {
    const iframe = document.querySelector('iframe');
    if (iframe !== null) {
        const iframeDocument =
            iframe.contentDocument === null ? iframe.contentWindow?.document : iframe.contentDocument;
        if (iframeDocument !== undefined) {
            const iframeHead = iframeDocument.head;

            const style = iframeDocument.createElement('style');
            style.type = 'text/css';

            const css = '.cke_editable.cke_display_version_check-absolute:before { display: none; }';
            style.appendChild(iframeDocument.createTextNode(css));

            iframeHead.appendChild(style);
        }
    }
};

export const Textarea = observer('Textarea', (props: PropsType) => {
    const {
        label,
        placeholder,
        formState,
        isError,
        isEmpty,
        required,
        maxLength,
        dataTest,
        ckeditor,
        isReadOnly = false,
        customErrorMessage,
        config = {},
    } = props;
    const { errorsForView, value, setValue, setAsVisited } = formState;

    const renderStatusMessage = (): Array<JSX.Element> | null => {
        if (customErrorMessage !== undefined) {
            return [customErrorMessage];
        }
        if (errorsForView.length > 0) {
            const messages = errorsForView.map((message) => (
                <InputError
                    key={message}
                    data-test='input-error'
                >
                    {message}
                </InputError>
            ));
            return messages;
        }
        return null;
    };

    const handleChangeCKEditor = (evt: CKEditor): void => {
        const newContent = evt.editor.getData();
        setValue(newContent);
    };

    const handleBlur = (): void => {
        setAsVisited();
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => setValue(e.target.value);

    const preventPaste = (evt: CKEditor): void => {
        const editor = evt.editor;
        editor.on('paste', (e: CKEditor.eventInfo) => {
            e.cancel();
        });
    };

    const renderEditor = (): JSX.Element => {
        if (ckeditor === true) {
            return (
                <div data-test={dataTest}>
                    <CKEditor
                        key='editor'
                        activeClass='p10'
                        content={value}
                        events={{
                            blur: handleBlur,
                            change: handleChangeCKEditor,
                            instanceReady: (evt: CKEditor) => {
                                hideErrorMessage();
                                if (isReadOnly) {
                                    preventPaste(evt);
                                }
                            },
                        }}
                        config={{
                            ...config,
                            readOnly: isReadOnly,
                        }}
                    />
                </div>
            );
        }

        return (
            <TextareaElement
                key='textarea'
                placeholder={placeholder}
                isError={isError}
                isEmpty={isEmpty}
                required={required}
                maxLength={maxLength}
                onBlur={handleBlur}
                onChange={handleChange}
                value={value}
                data-test={dataTest}
            />
        );
    };

    return (
        <Wrapper
            isEmpty={isEmpty}
            isReadOnly={isReadOnly}
        >
            {label === undefined ? null : <InputLabel isEmpty={isEmpty}>{label}</InputLabel>}
            {renderEditor()}
            {renderStatusMessage()}
        </Wrapper>
    );
});
